import type { ParseKeys } from 'i18next';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useResponsiveTabsWidth } from '@/hooks/useResponsiveTabsWidth';
import { OverviewTab } from '@/types/components';

interface OverviewTabItem {
  value: OverviewTab;
  labelKey: ParseKeys;
}

const MAX_TABS_WIDTH_PX = 400;

const TABS: OverviewTabItem[] = [
  { value: OverviewTab.Ratings, labelKey: 'overview.tabs.ratings' },
  { value: OverviewTab.Journals, labelKey: 'overview.tabs.journals' },
  { value: OverviewTab.Attendance, labelKey: 'overview.tabs.attendance' },
  { value: OverviewTab.Report, labelKey: 'overview.tabs.report' },
];

interface Props {
  value: OverviewTab;
  onChange: (tab: OverviewTab) => void;
}

export const OverviewTabs: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { ref, style } = useResponsiveTabsWidth({
    maxWidth: MAX_TABS_WIDTH_PX,
  });

  if (isMobile) {
    return <MobileTabs value={value} onChange={onChange} />;
  }

  const items = TABS.map((tab) => ({
    value: tab.value,
    label: t(tab.labelKey),
  }));

  return (
    <div ref={ref} style={style} className="w-fit">
      <SegmentedTabs
        items={items}
        value={value}
        className="flex w-full"
        onChange={onChange}
      />
    </div>
  );
};

const MobileTabs: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [value]);

  return (
    <div className="scrollbar-hidden w-full overflow-x-auto py-1">
      <ul className="flex h-10 w-fit items-center gap-1">
        {TABS.map((tab) => {
          const isActive = tab.value === value;
          return (
            <li key={tab.value} className="shrink-0">
              <button
                type="button"
                ref={isActive ? activeRef : undefined}
                onClick={() => onChange(tab.value)}
                className={`text-subhead-14 flex h-10 cursor-pointer items-center rounded-[20px] px-3 whitespace-nowrap transition-colors ${isActive ? 'bg-primary text-primary-foreground shadow-card' : 'text-muted-foreground'}`}
              >
                {t(tab.labelKey)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
